<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJson,
  postMarketplaceJsonBody,
  patchMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const data = ref(null);
const loading = ref(false);
const running = ref(false);
const errorMsg = ref('');
const date = ref(''); // boş = bugün

const recs = computed(() => data.value?.recommendations || []);
const kpis = computed(() => data.value?.kpis || {});
const seats = computed(() => data.value?.seats || []);
const library = computed(() => data.value?.library || {});
const blockedCount = computed(() => data.value?.blocked_count || 0);
const fullAuto = computed(() => data.value?.full_auto);
const events = computed(() => data.value?.events || []);
const libraryTotal = computed(() =>
  Object.values(library.value).reduce((t, n) => t + n, 0)
);
const usedBudget = computed(() =>
  seats.value.filter(s => !s.empty).reduce((t, s) => t + (s.daily_budget_total || 0), 0)
);

// Tahmini kitle büyüklüğü: 16200000 → "16,2 milyon", 10200 → "10 bin"
const formatReach = reach => {
  const n = reach?.ust || reach?.alt;
  if (!n) return '';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace('.', ',')} milyon`;
  if (n >= 1000) return `${Math.round(n / 1000)} bin`;
  return `${n}`;
};

const wheel = computed(() => data.value?.wheel);

// "?" yardım pencereleri — her blok için sade Türkçe açıklama.
const helpKey = ref(null);
const HELP = {
  mod: {
    title: 'Çalışma Modu',
    text: 'TAM OTOMATİK: sistem her sabah uygun yeni ürünü kendisi reklama çıkarır, kazananın bütçesini büyütür, 2 gün üst üste zarar edeni kendiliğinden durdurur. Sen hiçbir düğmeye basmazsın; her adım WhatsApp bildirimi olarak gelir. Onaylı Mod ise her kurulum için senin onayını bekler.',
  },
  ritim: {
    title: 'Günlük İşletim Ritmi',
    text: 'Sistemin bir günü: 06:40 — dünkü yeni Instagram Reels videoları 7 kontrolden geçer (maliyet kayıtlı mı, stok yeterli mi, zaten reklamı var mı...), uygun olan kitle istifiyle kurulup yayına girer. 07:20 — öğrenmesi biten Reels kampanyaları gerçek net kâr denetiminden geçer. 07:40 — Dönme Dolap bekçisi katalog çarkına bakar. 08:30 — sabah özeti WhatsApp\'ına düşer.',
  },
  butce: {
    title: 'Bağlı Bütçe',
    text: 'Pilotun şu an reklamlara bağladığı toplam günlük para. Tavan 1.500 TL/gün — sistem ne olursa olsun bunun üzerine çıkamaz. 5 koltuk = aynı anda en fazla 5 ürün kampanyası dönebilir.',
  },
  kutuphane: {
    title: 'Kitle Kütüphanesi',
    text: 'Reklamlarda kullanılan hazır hedef kitle kartları. Soğuk = bizi hiç tanımayanlar (ilgi alanına göre: Kadın Giyim, Eşofman...). RM (remarketing) = siteni ziyaret etmiş, sepete atmış, Instagram\'da etkileşmiş SICAK kişiler. Benzer (LAL) = müşterilerine benzeyen yeni insanlar (Meta bulur). Tuzak = geçmişte para yakan kitleler — kara listede, sistem asla kullanamaz. Her kartın geçmiş harcama/kâr sicili kayıtlıdır; seçimler bu sicile göre yapılır.',
  },
  danisman_kpi: {
    title: 'Bugünün Danışman Önerisi',
    text: 'SENİN elle açtığın reklamlar için sistemin sabah tavsiyesi: "aç" = bu ürünün videosu iyi gidiyor, reklama çıkarmaya değer; "durdur" = bu reklam zarar ettiriyor, kapatmayı düşün. Bunlar sadece öneridir — pilotun otomatik kararlarından tamamen ayrıdır, senin reklamlarına sistem asla kendisi dokunmaz.',
  },
  isabet: {
    title: 'Öneri İsabeti',
    text: 'Önerilere verdiğin 👍/👎 puanların özeti. %70 üzerindeyse motorun tavsiyeleri seninle aynı yönde düşünüyor demektir. Puanlamaya devam etmen motorun kalitesini ölçmemizi sağlar.',
  },
  koltuk: {
    title: 'Kampanya Koltukları',
    text: 'Reels reklamlarının ligi: 5 koltuk var, her koltukta bir ürün kampanyası oturur. Yeni ürün ancak boş koltuğa girer. 2 gün üst üste zarar eden kampanya koltuğunu kaybeder, kuyruktaki en iyi aday yerine geçer. Böylece para hep kötüden iyiye akar. "Öğrenmede" = ilk 72 saat, Meta algoritması hedef kitleyi tanıyor, bu sürede sistem dokunmaz.',
  },
  cark: {
    title: 'Dönme Dolap — Katalog Çarkı',
    text: 'Şirket var oldukça dönen, hiç kapanmayan katalog reklamı. 3 hat: Remarketing = ürüne bakan/sepete atan kişiye BAKTIĞI ürünü tekrar gösterir (en kârlı format). Benzer = müşterine benzeyenlere En Çok Satanlar vitrinini gösterir. Soğuk = yeni insanlara Kâr Şampiyonları vitrinini gösterir. 🌡 Yorgunluk termometresi: aynı insanlar reklamı çok görmeye başlayınca (sıklık artar, tıklama düşer, maliyet şişer) skor yükselir — sarıda kitleler tazelenir, kırmızıda hat komple yenilenir (yeni vitrin + yeni kitle + yeni metin). Vitrinler her Pazartesi satış verisiyle kendini günceller.',
  },
  danisman: {
    title: 'Danışman Önerileri',
    text: 'Her sabah 06:30\'da üretilen analiz: elle açtığın reklamların ve yeni Reels videolarının tek tek değerlendirmesi. Yeşil "REKLAM AÇARDIM" = sinyaller güçlü (izlenme, yorum, stok, marj). Kırmızı "DURDURURDUM" = para yakıyor. Kartlardaki 👍/👎 ile katılıp katılmadığını işaretlersin. Tarih oklarıyla eski günlere dönebilirsin.',
  },
  gunluk: {
    title: 'Pilot Günlüğü',
    text: 'Sistemin karar defteri: her kurulum, atlama, durdurma, bütçe değişikliği buraya sebebiyle yazılır. "Neden bu ürün reklama girmedi?" sorusunun cevabı hep buradadır — örn. "stok 32, eşik 40 altı" ya da "maliyet bilinmiyor". Hiçbir karar sebepsiz ve kayıtsız olmaz.',
  },
};

const VITRIN_LABELS = {
  all: 'Tüm Katalog (baktığı ürün)',
  en_cok_satan: 'En Çok Satanlar',
  iade_dostu: 'İade Dostu',
  kar_sampiyon: 'Kâr Şampiyonları',
  yeni_gelen: 'Yeni Gelenler',
  stok_eritme: 'Stok Eritme',
};

const HAT_STATUS = {
  active: { label: '▲ Dönüyor', cls: 'text-green-700 dark:text-green-400' },
  phase_out: { label: '⏳ Sönümleniyor', cls: 'text-yellow-700 dark:text-yellow-400' },
  sleeping: { label: '😴 Uykuda', cls: 'text-n-slate-10' },
  paused: { label: '⏸ Duraklatıldı', cls: 'text-yellow-700 dark:text-yellow-400' },
};

// Yorgunluk termometresi: null = veri toplanıyor, <30 yeşil, <60 sarı, 60+ kırmızı
const fatigueUi = skor => {
  if (skor === null || skor === undefined)
    return { label: 'veri toplanıyor', cls: 'bg-n-solid-3 text-n-slate-10' };
  if (skor < 30)
    return { label: `dinç · ${skor}/100`, cls: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
  if (skor < 60)
    return { label: `yoruluyor · ${skor}/100`, cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' };
  return { label: `yorgun · ${skor}/100`, cls: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
};

const SEAT_STATUS = {
  sanal: { label: '🛠 Kurulum planlandı', cls: 'text-yellow-700 dark:text-yellow-400' },
  paused: { label: '⏸ Kurulu — yayın sırasında', cls: 'text-yellow-700 dark:text-yellow-400' },
  learning: { label: '◆ Öğrenmede (72 saat)', cls: 'text-yellow-700 dark:text-yellow-400' },
  active: { label: '▲ Yayında — günlük denetimde', cls: 'text-green-700 dark:text-green-400' },
  killed: { label: '■ Kapatıldı', cls: 'text-red-600 dark:text-red-400' },
  frozen: { label: '🧊 Donduruldu', cls: 'text-n-slate-10' },
};

const rateCampaign = async (seat, value) => {
  const next = seat.feedback === value ? null : value;
  try {
    await patchMarketplaceJson(accountId, 'ad_pilot/campaign_feedback', {
      campaign_id: seat.id,
      value: next,
    });
    seat.feedback = next;
  } catch (e) {
    errorMsg.value = 'Puan kaydedilemedi.';
  }
};

// ADIM 2 — beğenilen sanal kurulumu Meta'da PAUSED kurar / onaylayıp başlatır.
const launchingId = ref(null);

const launchReal = async seat => {
  errorMsg.value = '';
  launchingId.value = seat.id;
  try {
    await postMarketplaceJsonBody(accountId, 'ad_pilot/launch_real', {
      campaign_id: seat.id,
    });
    await load();
  } catch (e) {
    errorMsg.value = e?.message || 'Meta kurulumu başarısız — tekrar dene.';
  } finally {
    launchingId.value = null;
  }
};

const activateCampaign = async seat => {
  errorMsg.value = '';
  launchingId.value = seat.id;
  try {
    await postMarketplaceJsonBody(accountId, 'ad_pilot/activate', {
      campaign_id: seat.id,
    });
    await load();
  } catch (e) {
    errorMsg.value = e?.message || 'Aktivasyon başarısız — tekrar dene.';
  } finally {
    launchingId.value = null;
  }
};

const VERDICT_UI = {
  ac: { label: '▲ REKLAM AÇARDIM', cls: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  durdur: { label: '■ DURDURURDUM', cls: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  izle: { label: '◆ İZLEMEDE', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const q = date.value ? `ad_pilot?date=${date.value}` : 'ad_pilot';
    data.value = await fetchMarketplaceJson(accountId, q);
    if (!date.value) date.value = data.value.date;
  } catch (e) {
    errorMsg.value = 'Pilot verisi yüklenemedi.';
  } finally {
    loading.value = false;
  }
};

const shiftDay = async delta => {
  const d = new Date(date.value);
  d.setDate(d.getDate() + delta);
  date.value = d.toISOString().slice(0, 10);
  await load();
};

const runNow = async () => {
  running.value = true;
  try {
    await postMarketplaceJson(accountId, 'ad_pilot/run');
    // Motor 1-2 dk sürer; kullanıcıya bilgi ver, sonra tazele.
    setTimeout(() => {
      load();
      running.value = false;
    }, 90000);
  } catch (e) {
    running.value = false;
    errorMsg.value = 'Analiz başlatılamadı.';
  }
};

const rate = async (rec, value) => {
  const next = rec.feedback === value ? null : value;
  try {
    const res = await patchMarketplaceJson(accountId, 'ad_pilot/feedback', {
      rec_id: rec.id,
      value: next,
    });
    rec.feedback = next;
    if (data.value) data.value.kpis = res.kpis;
  } catch (e) {
    errorMsg.value = 'Puan kaydedilemedi.';
  }
};

const trDate = d =>
  d
    ? new Date(d).toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        weekday: 'long',
      })
    : '';

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="max-w-6xl w-full mx-auto px-6 py-8">
      <!-- Başlık -->
      <div class="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div class="text-xs font-semibold uppercase tracking-widest text-n-slate-10">
            Koza Butik · ModaXlarge — Reklam Otomasyonu
          </div>
          <h1 class="text-3xl font-semibold text-n-slate-12 mt-1">
            🛩️ Reklam Pilotu
          </h1>
        </div>
        <div class="flex items-center gap-3">
          <span
            class="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm"
            :class="fullAuto ? 'border-green-600 bg-green-50 dark:bg-green-950' : 'border-n-weak bg-n-solid-1'"
          >
            <span class="w-2.5 h-2.5 rounded-full animate-pulse" :class="fullAuto ? 'bg-green-500' : 'bg-yellow-500'" />
            <b :class="fullAuto ? 'text-green-800 dark:text-green-300' : ''">{{ fullAuto ? 'TAM OTOMATİK' : 'Onaylı Mod' }}</b>
            <span class="text-n-slate-10">{{ fullAuto ? '· açar, ölçer, durdurur' : '· onayınla çalışır' }}</span>
            <button class="w-5 h-5 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold flex-none" @click="helpKey = 'mod'">?</button>
          </span>
          <button
            class="rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
            :disabled="running"
            @click="runNow"
          >
            {{ running ? 'Analiz ediyor… (~2 dk)' : 'Şimdi analiz et' }}
          </button>
        </div>
      </div>

      <!-- Günlük işletim ritmi -->
      <div class="relative rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4 mb-6">
        <button class="absolute top-3 right-3 w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'ritim'">?</button>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div class="font-semibold text-n-slate-12">🌅 06:40 — Kurulum</div>
            <p class="text-n-slate-11 mt-0.5">Yeni Reels 7 kontrolden geçer; uygunsa kitle istifiyle kurulur{{ fullAuto ? ' ve yayına girer' : ', onayına sunulur' }}.</p>
          </div>
          <div>
            <div class="font-semibold text-n-slate-12">🛡 07:20 — Denetim</div>
            <p class="text-n-slate-11 mt-0.5">Öğrenmesi bitenlerde net kâr denetimi: 2 gün eksi → durdurulur, 2 gün artı → bütçe +%25.</p>
          </div>
          <div>
            <div class="font-semibold text-n-slate-12">📱 08:30 — Rapor</div>
            <p class="text-n-slate-11 mt-0.5">WhatsApp özeti; ayrıca her kurulum/durdurma anında ayrı bildirim gelir.</p>
          </div>
          <div>
            <div class="font-semibold text-n-slate-12">🔒 Emniyet</div>
            <p class="text-n-slate-11 mt-0.5">Tavan 1.500 TL/gün · ölçü gerçek net kâr · pixel arızasında her şey donar.</p>
          </div>
        </div>
      </div>

      <!-- KPI -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex justify-between items-center">
            <span class="text-xs text-n-slate-10">Bağlı bütçe</span>
            <button class="w-5 h-5 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'butce'">?</button>
          </div>
          <div class="text-2xl font-semibold text-n-slate-12 tabular-nums">
            {{ usedBudget.toLocaleString('tr-TR') }} <span class="text-sm text-n-slate-10">/ 1.500 TL</span>
          </div>
          <div class="text-xs text-n-slate-10">{{ seats.filter(s => !s.empty).length }} / 5 koltuk dolu</div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex justify-between items-center">
            <span class="text-xs text-n-slate-10">Kitle kütüphanesi</span>
            <button class="w-5 h-5 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'kutuphane'">?</button>
          </div>
          <div class="text-2xl font-semibold text-n-slate-12 tabular-nums">{{ libraryTotal }} kitle</div>
          <div class="text-xs text-n-slate-10">
            {{ library.soguk || 0 }} soğuk · {{ library.remarketing || 0 }} RM · {{ library.lookalike || 0 }} benzer
            <template v-if="blockedCount"> · {{ blockedCount }} tuzak kilitli</template>
          </div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex justify-between items-center">
            <span class="text-xs text-n-slate-10">Bugünün danışman önerisi</span>
            <button class="w-5 h-5 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'danisman_kpi'">?</button>
          </div>
          <div class="text-2xl font-semibold text-n-slate-12 tabular-nums">
            {{ kpis.today_ac || 0 }} aç · {{ kpis.today_durdur || 0 }} durdur
          </div>
          <div class="text-xs text-n-slate-10">{{ kpis.today_izle || 0 }} izlemede · elle açtığın reklamlar için</div>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex justify-between items-center">
            <span class="text-xs text-n-slate-10">Öneri isabeti (14 gün)</span>
            <button class="w-5 h-5 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'isabet'">?</button>
          </div>
          <div class="text-2xl font-semibold tabular-nums" :class="kpis.hit_rate >= 70 ? 'text-green-600 dark:text-green-400' : 'text-n-slate-12'">
            {{ kpis.hit_rate === null || kpis.hit_rate === undefined ? '—' : `%${kpis.hit_rate}` }}
          </div>
          <div class="text-xs text-n-slate-10">{{ kpis.rated_count || 0 }} öneri puanlandı</div>
        </div>
      </div>

      <!-- KOLTUK TAHTASI -->
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-xl font-semibold text-n-slate-12 flex items-center gap-2">
          Kampanya Koltukları
          <button class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'koltuk'">?</button>
        </h2>
        <span class="text-sm text-n-slate-10">5 koltuk · kötü performans koltuğu kaybeder, sıradaki ürün girer</span>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div
          v-for="seat in seats"
          :key="seat.seat_no"
          class="rounded-xl border p-3 text-sm"
          :class="seat.empty ? 'border-dashed border-n-weak text-n-slate-10 flex items-center justify-center min-h-[110px]'
            : seat.status === 'sanal' ? 'border-yellow-500 bg-n-solid-1' : 'border-n-weak bg-n-solid-1'"
        >
          <template v-if="seat.empty">Koltuk {{ seat.seat_no }}<br />boş</template>
          <template v-else>
            <div class="text-[11px] uppercase tracking-wider text-n-slate-10">Koltuk {{ seat.seat_no }} · {{ seat.day_no }}. gün</div>
            <div class="font-semibold text-n-slate-12 mt-0.5">{{ seat.product_code }}</div>
            <div class="text-xs text-n-slate-11 tabular-nums">{{ seat.daily_budget_total }} TL/gün</div>
            <div class="text-xs mt-1" :class="SEAT_STATUS[seat.status]?.cls">{{ SEAT_STATUS[seat.status]?.label || seat.status }}</div>
          </template>
        </div>
      </div>

      <!-- 🎡 DÖNME DOLAP — katalog çarkı -->
      <template v-if="wheel">
        <div class="flex items-center justify-between mb-3 mt-8">
          <h2 class="text-xl font-semibold text-n-slate-12 flex items-center gap-2">
            🎡 Dönme Dolap — Katalog Çarkı
            <button class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'cark'">?</button>
          </h2>
          <span class="text-sm text-n-slate-11 tabular-nums">
            <b :class="wheel.status === 'active' ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
              {{ wheel.status === 'active' ? '▲ Dönüyor' : wheel.status === 'frozen' ? '🧊 Donduruldu' : wheel.status }}
            </b>
            · {{ wheel.daily_budget_total }} TL/gün (tavan 500)
          </span>
        </div>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 mb-2">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div v-for="h in wheel.hats" :key="h.family" class="rounded-xl bg-n-solid-2 p-4 flex flex-col gap-2">
              <div class="flex items-start justify-between gap-2">
                <div class="font-semibold text-n-slate-12 capitalize">{{ h.name }}</div>
                <span class="rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap" :class="fatigueUi(h.yorgunluk).cls">
                  🌡 {{ fatigueUi(h.yorgunluk).label }}
                </span>
              </div>
              <div class="text-sm text-n-slate-11 tabular-nums">
                {{ h.budget }} TL/gün ·
                <span :class="HAT_STATUS[h.status]?.cls">{{ HAT_STATUS[h.status]?.label || h.status }}</span>
              </div>
              <div class="text-sm">
                <span class="text-n-slate-10">Vitrin:</span>
                <b class="text-n-slate-12">{{ VITRIN_LABELS[h.vitrin] || h.vitrin }}</b>
                <span class="text-xs text-n-slate-10"> · metin {{ (h.metin_no || 0) + 1 }}/5</span>
              </div>
              <div class="flex flex-wrap gap-1">
                <span v-for="a in h.audiences" :key="a" class="rounded-full bg-n-solid-3 px-2 py-0.5 text-xs font-semibold text-n-slate-12">{{ a }}</span>
              </div>
              <div v-if="h.yorgunluk_detay" class="text-xs text-n-slate-10">{{ h.yorgunluk_detay }}</div>
            </div>
          </div>
        </div>
        <p class="text-xs text-n-slate-10 mb-8">
          Şirket var oldukça dönen katalog reklamı. Bekçi her sabah 07:40'ta bakar: yorulan hattın kitlesi/vitrini otomatik tazelenir,
          2 gün kâr edene bütçe artar, 2 gün zarar edenin bütçesi kısılır (tabana inen 5 gün uyur). Vitrinler her Pazartesi satış verisiyle yenilenir.
        </p>
      </template>

      <!-- KURU PROVA KARTLARI -->
      <div v-for="seat in seats.filter(s => !s.empty && s.status === 'sanal')" :key="'dry' + seat.id"
        class="rounded-2xl border-2 border-yellow-500 bg-n-solid-1 p-5 mb-6"
      >
        <div class="flex items-start justify-between gap-3 mb-3">
          <span class="rounded-full px-3 py-1 text-xs font-bold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">🛠 KURULUM PLANI — Meta'ya yazılmadı, 0 TL</span>
          <span class="flex gap-2">
            <button class="rounded-full border px-4 py-1.5 text-sm font-semibold"
              :class="seat.feedback === 'up' ? 'bg-green-600 border-green-600 text-white' : 'border-n-weak text-n-slate-11 hover:border-green-600'"
              @click="rateCampaign(seat, 'up')">👍 Kurulumu beğendim</button>
            <button class="rounded-full border px-3 py-1.5 text-sm font-semibold"
              :class="seat.feedback === 'down' ? 'bg-red-600 border-red-600 text-white' : 'border-n-weak text-n-slate-11 hover:border-red-600'"
              @click="rateCampaign(seat, 'down')">👎</button>
          </span>
        </div>
        <p v-if="fullAuto" class="text-xs text-n-slate-10 mb-2">
          Tam otomatik modda bu plan normalde kendiliğinden yayına girer; burada görünüyorsa kuruluma engel bir durum var (WhatsApp'ına bildirildi) — aşağıdan elle sürdürebilirsin.
        </p>
        <div class="font-semibold text-n-slate-12 text-lg mb-2">{{ seat.name }}</div>
        <div class="rounded-xl bg-n-solid-2 px-4 py-3 text-sm flex flex-col gap-1.5 mb-3">
          <div v-for="s in seat.sets" :key="s.name" class="flex flex-col gap-1.5 py-1">
            <div class="flex justify-between gap-3">
              <span class="text-n-slate-10">{{ s.name }} ({{ s.budget }} TL)</span>
              <span v-if="formatReach(s.reach)" class="text-xs text-n-slate-10">🎯 ~{{ formatReach(s.reach) }} kişi</span>
            </div>
            <div class="flex flex-wrap gap-1">
              <span v-for="a in s.audiences" :key="a" class="rounded-full bg-n-solid-3 px-2 py-0.5 text-xs font-semibold text-n-slate-12">{{ a }}</span>
            </div>
          </div>
        </div>
        <ul class="flex flex-col gap-1">
          <li v-for="(n, i) in seat.rotation_notes" :key="i" class="text-xs text-n-slate-11 pl-4 relative">
            <span class="absolute left-0 top-[5px] w-1.5 h-1.5 rounded-sm bg-yellow-500" />{{ n }}
          </li>
        </ul>
        <div v-if="seat.feedback === 'up'" class="mt-4 flex items-center gap-3">
          <button
            class="rounded-full bg-woot-600 hover:bg-woot-700 text-white px-5 py-2 text-sm font-bold disabled:opacity-50"
            :disabled="launchingId === seat.id"
            @click="launchReal(seat)"
          >
            {{ launchingId === seat.id ? 'Meta\'da kuruluyor…' : '🚀 Meta\'da Kur (PAUSED — para harcamaz)' }}
          </button>
          <span class="text-xs text-n-slate-10">Kurulum duraklatılmış açılır; sen onaylamadan yayına girmez.</span>
        </div>
      </div>

      <!-- META'DA KURULU (PAUSED) KARTLAR -->
      <div v-for="seat in seats.filter(s => !s.empty && s.status === 'paused')" :key="'paused' + seat.id"
        class="rounded-2xl border-2 border-woot-500 bg-n-solid-1 p-5 mb-6"
      >
        <div class="flex items-start justify-between gap-3 mb-3">
          <span class="rounded-full px-3 py-1 text-xs font-bold bg-woot-100 text-woot-800 dark:bg-woot-900 dark:text-woot-200">⏸ META'DA KURULU — duraklatılmış, para harcanmıyor</span>
        </div>
        <div class="font-semibold text-n-slate-12 text-lg mb-2">{{ seat.name }}</div>
        <div class="rounded-xl bg-n-solid-2 px-4 py-3 text-sm flex flex-col gap-1.5 mb-4">
          <div v-for="s in seat.sets" :key="s.name" class="flex flex-col gap-1.5 py-1">
            <div class="flex justify-between gap-3">
              <span class="text-n-slate-10">{{ s.name }} ({{ s.budget }} TL)</span>
              <span v-if="formatReach(s.reach)" class="text-xs text-n-slate-10">🎯 ~{{ formatReach(s.reach) }} kişi</span>
            </div>
            <div class="flex flex-wrap gap-1">
              <span v-for="a in s.audiences" :key="a" class="rounded-full bg-n-solid-3 px-2 py-0.5 text-xs font-semibold text-n-slate-12">{{ a }}</span>
            </div>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <button
            class="rounded-full bg-green-600 hover:bg-green-700 text-white px-5 py-2 text-sm font-bold disabled:opacity-50"
            :disabled="launchingId === seat.id"
            @click="activateCampaign(seat)"
          >
            {{ launchingId === seat.id ? 'Başlatılıyor…' : `✅ Onayla & Başlat (${seat.daily_budget_total} TL/gün)` }}
          </button>
          <span class="text-xs text-n-slate-10">Başlatınca 72 saat öğrenme dönemi: pilot dokunmaz, sabah raporunda izlersin.</span>
        </div>
      </div>

      <!-- Gün başlığı -->
      <div class="flex items-center justify-between mb-1">
        <h2 class="text-xl font-semibold text-n-slate-12 flex items-center gap-2">
          Danışman Önerileri
          <button class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'danisman'">?</button>
        </h2>
        <div class="flex items-center gap-2 text-sm">
          <button class="rounded-lg border border-n-weak px-2 py-1 hover:bg-n-solid-2" @click="shiftDay(-1)">‹</button>
          <span class="text-n-slate-11">{{ trDate(date) }}</span>
          <button class="rounded-lg border border-n-weak px-2 py-1 hover:bg-n-solid-2" @click="shiftDay(1)">›</button>
        </div>
      </div>
      <p class="text-sm text-n-slate-10 mb-4">
        Elle açtığın kampanyalar için her sabah 06:30'da üretilen aç/durdur analizi — pilotun otomatik kararlarından ayrıdır. 👍/👎 puanların motorun isabetini ölçmeye devam eder.
      </p>

      <div v-if="errorMsg" class="rounded-lg bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-4 py-3 mb-4 text-sm">
        {{ errorMsg }}
      </div>
      <div v-if="loading" class="text-n-slate-11 py-10 text-center">Yükleniyor…</div>
      <div v-else-if="!recs.length" class="rounded-xl border border-dashed border-n-weak p-10 text-center text-n-slate-10">
        Bu gün için öneri yok. Gece 06:30'da otomatik üretilir — ya da "Şimdi analiz et"e bas.
      </div>

      <!-- Öneri kartları -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div
          v-for="rec in recs"
          :key="rec.id"
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 flex flex-col gap-3 shadow-sm"
        >
          <div class="flex items-start justify-between gap-3">
            <span class="rounded-full px-3 py-1 text-xs font-bold" :class="VERDICT_UI[rec.verdict].cls">
              {{ VERDICT_UI[rec.verdict].label }}
            </span>
            <span class="text-xs text-n-slate-10 text-right">
              Güven<br />
              <b class="text-sm" :class="rec.confidence === 'yuksek' ? 'text-yellow-700 dark:text-yellow-400' : 'text-n-slate-11'">
                {{ rec.confidence === 'yuksek' ? 'Yüksek' : 'Orta' }}
              </b>
            </span>
          </div>

          <div class="flex items-center gap-3">
            <img
              v-if="rec.thumbnail_url"
              :src="rec.thumbnail_url"
              class="w-14 h-14 rounded-xl object-cover flex-none"
              alt=""
            />
            <div v-else class="w-14 h-14 rounded-xl bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300 flex items-center justify-center font-bold flex-none">
              {{ rec.product_code || '—' }}
            </div>
            <div class="min-w-0">
              <div class="font-semibold text-n-slate-12 leading-snug truncate">
                {{ rec.product_code }} — {{ rec.title }}
              </div>
              <a
                v-if="rec.media_permalink"
                :href="rec.media_permalink"
                target="_blank"
                rel="noopener"
                class="text-xs text-violet-600 dark:text-violet-400 hover:underline"
              >Gönderiyi aç ↗</a>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-x-5 gap-y-1 rounded-xl bg-n-solid-2 px-4 py-3 text-sm tabular-nums">
            <template v-if="rec.verdict !== 'durdur'">
              <div class="flex justify-between"><span class="text-n-slate-10">İzlenme</span><b>{{ (rec.signals.izlenme || 0).toLocaleString('tr-TR') }}</b></div>
              <div class="flex justify-between"><span class="text-n-slate-10">Yorum</span><b>{{ rec.signals.yorum }}</b></div>
              <div class="flex justify-between"><span class="text-n-slate-10">Stok</span><b :class="(rec.signals.stok || 0) < 40 ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'">{{ rec.signals.stok }}</b></div>
              <div class="flex justify-between"><span class="text-n-slate-10">Net marj</span><b>{{ rec.signals.marj ? `%${rec.signals.marj}` : 'doğrulanacak' }}</b></div>
            </template>
            <template v-else>
              <div class="flex justify-between"><span class="text-n-slate-10">Mesaj maliyeti</span><b class="text-red-600 dark:text-red-400">{{ rec.signals.mesaj_maliyeti ? `${rec.signals.mesaj_maliyeti} TL` : '∞' }}</b></div>
              <div class="flex justify-between"><span class="text-n-slate-10">Mağaza medyanı</span><b>{{ rec.signals.magaza_medyani }} TL</b></div>
              <div class="flex justify-between"><span class="text-n-slate-10">5 gün harcama</span><b>{{ rec.signals.harcama_5g }} TL</b></div>
              <div class="flex justify-between"><span class="text-n-slate-10">5 gün mesaj</span><b>{{ rec.signals.mesaj_5g }}</b></div>
            </template>
          </div>

          <ul class="flex flex-col gap-1.5">
            <li v-for="(g, i) in rec.reasons" :key="i" class="text-sm text-n-slate-11 pl-5 relative">
              <span class="absolute left-0 top-[7px] w-2 h-2 rounded-sm bg-violet-500" />
              {{ g }}
            </li>
          </ul>

          <div class="flex items-center justify-between gap-3 border-t border-n-weak pt-3 mt-auto">
            <span class="text-sm text-n-slate-11">Plan: <b class="text-n-slate-12">{{ rec.plan }}</b></span>
            <span class="flex gap-2">
              <button
                class="rounded-full border px-4 py-1.5 text-sm font-semibold"
                :class="rec.feedback === 'up'
                  ? 'bg-green-600 border-green-600 text-white'
                  : 'border-n-weak text-n-slate-11 hover:border-green-600 hover:text-green-700'"
                @click="rate(rec, 'up')"
              >👍 Katılıyorum</button>
              <button
                class="rounded-full border px-3 py-1.5 text-sm font-semibold"
                :class="rec.feedback === 'down'
                  ? 'bg-red-600 border-red-600 text-white'
                  : 'border-n-weak text-n-slate-11 hover:border-red-600 hover:text-red-700'"
                @click="rate(rec, 'down')"
              >👎</button>
            </span>
          </div>
        </div>
      </div>

      <!-- PİLOT GÜNLÜĞÜ — pilotun her kararı, nedeniyle -->
      <div class="mt-10">
        <h2 class="text-xl font-semibold text-n-slate-12 mb-1 flex items-center gap-2">
          🛰 Pilot Günlüğü
          <button class="w-6 h-6 rounded-full border border-n-weak text-n-slate-10 hover:bg-n-solid-3 text-xs font-bold" @click="helpKey = 'gunluk'">?</button>
        </h2>
        <p class="text-sm text-n-slate-10 mb-4">
          Pilot her sabah 06:40'ta adayları 7 kontrolden geçirir; burada her kararını ve nedenini görürsün — atladıkları dahil.
        </p>
        <div class="rounded-2xl border border-n-weak bg-n-solid-1 divide-y divide-n-weak">
          <div v-for="(ev, i) in events" :key="i" class="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm">
            <span class="text-n-slate-10 tabular-nums shrink-0 w-24">{{ ev.at }}</span>
            <span class="font-semibold text-n-slate-12 shrink-0">{{ ev.action }}<template v-if="ev.subject"> · {{ ev.subject }}</template></span>
            <span class="text-n-slate-11">{{ ev.reason }}</span>
          </div>
          <div v-if="!events.length" class="px-4 py-3 text-sm text-n-slate-10">Henüz kayıt yok.</div>
        </div>
      </div>

      <p class="text-xs text-n-slate-10 mt-8">
        Pilot yalnızca kendi açtığı ("interaktif.tech otomasyon" önekli) kampanyalara dokunur; elle açtığın reklamlar için yalnızca öneri üretir.
        Tüm kararlar gerçek net kâr ölçüsüyle verilir ve Pilot Günlüğü'nde gerekçesiyle kayıt altındadır.
      </p>
    </div>

    <!-- "?" AÇIKLAMA PENCERESİ -->
    <div
      v-if="helpKey"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6"
      @click.self="helpKey = null"
    >
      <div class="max-w-xl w-full rounded-2xl bg-n-solid-1 border border-n-weak p-6 shadow-xl">
        <div class="flex items-start justify-between gap-4 mb-3">
          <h3 class="text-2xl font-semibold text-n-slate-12">💡 {{ HELP[helpKey].title }}</h3>
          <button
            class="rounded-full border border-n-weak px-3 py-1 text-sm font-semibold text-n-slate-11 hover:bg-n-solid-3"
            @click="helpKey = null"
          >Kapat ✕</button>
        </div>
        <p class="text-lg leading-relaxed text-n-slate-12">{{ HELP[helpKey].text }}</p>
      </div>
    </div>
  </div>
</template>
